// src/components/Atelier4/ModesOperatoiresDisplay.js
// Component to display different attack path varieties (modes operatoires) and EBIOS RM phases
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  GitBranch,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Eye,
  Layers,
  Shield,
  Target,
  ExternalLink,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  getVraisemblanceLabel,
  getVraisemblanceColor,
  getVraisemblanceBgColor,
} from "../../utils/vraisemblanceCalculator";
import ModesOperatoiresFlowVisualization from "./ModesOperatoiresFlowVisualization";
import {
  generateModesOperatoiresFromActions,
  extractElementaryActions,
} from "../../utils/scenarioDataNormalizer";

/**
 * Component to display CTI context for a specific technique
 */
const CTIContext = ({ techniqueName, ctiData }) => {
  if (!ctiData || !techniqueName) return null;

  // Find matching technique in CTI data
  // We try to match by name or ID if available
  const matchingTechnique = ctiData.attackTechniques?.find(
    (t) =>
      t.name?.toLowerCase() === techniqueName.toLowerCase() ||
      t.name?.toLowerCase().includes(techniqueName.toLowerCase()) ||
      techniqueName.toLowerCase().includes(t.name?.toLowerCase()),
  );

  if (!matchingTechnique) return null;

  return (
    <div className="mt-2 bg-indigo-50 rounded-md p-2.5 border border-indigo-100 text-xs">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-indigo-800 font-semibold">
          <Shield className="w-3.5 h-3.5" />
          <span>
            Preuve CTI: {matchingTechnique.mitreId || matchingTechnique.name}
          </span>
        </div>
        {matchingTechnique.url && (
          <a
            href={matchingTechnique.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-800"
            title="Voir la source"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {matchingTechnique.description && (
        <div className="text-indigo-700 mb-2 leading-relaxed">
          {matchingTechnique.description.length > 150
            ? `${matchingTechnique.description.substring(0, 150)}...`
            : matchingTechnique.description}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {matchingTechnique.platforms?.slice(0, 3).map((p, i) => (
          <span
            key={i}
            className="px-1.5 py-0.5 bg-white rounded border border-indigo-200 text-[10px] text-indigo-600 font-medium"
          >
            {p}
          </span>
        ))}
        {matchingTechnique.detection && (
          <span className="px-1.5 py-0.5 bg-indigo-100 rounded border border-indigo-200 text-[10px] text-indigo-700 font-medium flex items-center gap-1">
            <Eye className="w-3 h-3" />
            Détectable
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Unified Modes Operatoires & Phases Display Component
 * Shows different attack path varieties with their risk profiles
 * Falls back to showing EBIOS RM phases if no modes operatoires exist
 */
const ModesOperatoiresDisplay = ({
  modesOperatoires: propModesOperatoires = [],
  vizModes = null,
  scenario = null,
  operationalSteps = [],
  elementaryActions: propElementaryActions = [],
  selectedModeId = null,
  onSelectMode = null,
  showPhases = false,
  onPhaseVisualization = null,
  selectedAttackPath = null,
  className = "",
  onDeleteMode = null,
  onToggleRetained = null,
  onCaptureReady = null,
}) => {
  const { t } = useTranslation();

  // Extract and normalize elementary actions from different sources
  const elementaryActions = useMemo(() => {
    // First try prop elementaryActions
    if (propElementaryActions && propElementaryActions.length > 0) {
      return propElementaryActions;
    }
    // If not provided, try to extract from operationalSteps (nested structure)
    const extracted = extractElementaryActions({
      operationalSteps,
      elementaryActions: propElementaryActions,
    });
    return extracted.length > 0 ? extracted : operationalSteps;
  }, [propElementaryActions, operationalSteps]);

  // Generate modesOperatoires if not provided but elementaryActions exist
  const modesOperatoires = useMemo(() => {
    if (propModesOperatoires && propModesOperatoires.length > 0) {
      return propModesOperatoires;
    }
    // Try to generate from elementary actions
    if (elementaryActions && elementaryActions.length > 0) {
      const generated = generateModesOperatoiresFromActions(elementaryActions);
      if (generated.length > 0) {
        console.log(
          "[ModesOperatoiresDisplay] Generated modesOperatoires from elementaryActions:",
          generated.length,
        );
        return generated;
      }
    }
    return [];
  }, [propModesOperatoires, elementaryActions]);

  const [expandedModes, setExpandedModes] = useState(
    new Set([modesOperatoires[0]?.id]),
  );

  // Determine which data to use
  const hasModesOperatoires = modesOperatoires && modesOperatoires.length > 0;
  const hasElementaryActions =
    elementaryActions && elementaryActions.length > 0;
  const actionsSource = hasElementaryActions
    ? elementaryActions
    : operationalSteps;

  // Stable step list for the flow visualization (new array every render caused
  // the PNG auto-capture loop — the visualization re-armed its timer on each render)
  const visualizationSteps = useMemo(() => {
    const deduped = new Map();
    [...actionsSource, ...operationalSteps].forEach((s, idx) => {
      deduped.set(String(s.elementaryActionId || s.id || s._id || idx), s);
    });
    return [...deduped.values()];
  }, [actionsSource, operationalSteps]);

  // Helper functions
  // Global mode number within the full combined list (manual + AI)
  const globalModeNumber = (modeId) => {
    const list = vizModes && vizModes.length > 0 ? vizModes : modesOperatoires;
    const idx = list.findIndex((m) => m.id === modeId);
    return idx === -1 ? 0 : idx;
  };

  const toggleExpand = (modeId) => {
    const newExpanded = new Set(expandedModes);
    if (newExpanded.has(modeId)) {
      newExpanded.delete(modeId);
    } else {
      newExpanded.add(modeId);
    }
    setExpandedModes(newExpanded);
  };

  console.log('[DEBUG-MOD] operationalSteps len:', operationalSteps.length, 'propElementaryActions len:', propElementaryActions.length);
  console.log('[DEBUG-MOD] first 5 operationalSteps elementaryActionIds:', operationalSteps.slice(0, 5).map(s => s.elementaryActionId));
  if (propElementaryActions.length > 0) {
    console.log('[DEBUG-MOD] first 5 propElementaryActions elementaryActionIds:', propElementaryActions.slice(0, 5).map(s => s.elementaryActionId));
  }

  const getActionDetails = (actionId) => {
    const sid = String(actionId);
    const action = actionsSource.find(
      (step) =>
        String(step.elementaryActionId) === sid ||
        String(step.id) === sid ||
        String(step._id) === sid,
    );
    if (action) return action;
    // Fallback: search in operationalSteps directly (catches new AI actions not yet in elementaryActions)
    return operationalSteps.find(
      (step) =>
        String(step.elementaryActionId) === sid ||
        String(step.id) === sid ||
        String(step._id) === sid,
    );
  };

  const getRiskIcon = (vraisemblance) => {
    if (vraisemblance >= 4) return <AlertTriangle className="w-4 h-4" />;
    if (vraisemblance >= 3) return <TrendingUp className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const getRiskLevel = (vraisemblance) => {
    if (vraisemblance >= 4)
      return t("workshop4.modesOperatoires.critical", "Critical");
    if (vraisemblance >= 3) return t("workshop4.modesOperatoires.high", "High");
    if (vraisemblance >= 2)
      return t("workshop4.modesOperatoires.medium", "Medium");
    return t("workshop4.modesOperatoires.low", "Low");
  };

  // If no modes operatoires, return null or empty state
  if (!hasModesOperatoires) {
    return (
      <div
        className={`p-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 ${className}`}
      >
        <div className="text-center text-slate-500">
          <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium">
            {t(
              "workshop4.modesOperatoires.noModeAvailable",
              "No operational mode available",
            )}
          </p>
          <p className="text-xs mt-1">
            {t(
              "workshop4.modesOperatoires.modesWillBeGenerated",
              "Operational modes will be generated automatically",
            )}
          </p>
        </div>
      </div>
    );
  }

  const retainedCount = modesOperatoires.filter((m) => m.retained !== false).length;
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header with Graph View Button */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-800">
            {t("workshop4.modesOperatoires.modes", "Operational Modes")} (
            {modesOperatoires.length})
          </h3>
          {onToggleRetained && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700">
              {retainedCount} {t('workshop4.modesOperatoires.retained', 'Retenu').toLowerCase()}
            </span>
          )}
        </div>
        {onToggleRetained && retainedCount === 0 && modesOperatoires.length > 0 && (
          <span className="text-[11px] text-amber-600 italic">
            {t('workshop4.modesOperatoires.noRetainedModes', 'Aucun mode retenu — vraisemblance neutralisée')}
          </span>
        )}
      </div>

      {/* Scrollable mode list when >6, accordion when ≤6 */}
      {modesOperatoires.length > 6 ? (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Scrollable mode header list */}
          <div className="lg:w-72 flex-shrink-0 max-h-[500px] overflow-y-auto pr-1 space-y-1">
            {modesOperatoires.map((mode, index) => {
              const isActive =
                expandedModes.has(mode.id) || selectedModeId === mode.id;
              const vraisemblance = mode.vraisemblance || 2;
              const vColor = getVraisemblanceColor(vraisemblance);
              const vBgColor = getVraisemblanceBgColor(vraisemblance);
              const riskLevel = getRiskLevel(vraisemblance);

              const isRetained = mode.retained !== false;
              return (
                <button
                  key={mode.id}
                  onClick={() => {
                    if (onSelectMode) onSelectMode(mode.id);
                    setExpandedModes(new Set([mode.id]));
                  }}
                  className={`w-full text-left p-2.5 rounded-lg border-2 transition-all ${
                    isActive
                      ? "border-indigo-400 bg-indigo-50 shadow-sm"
                      : isRetained
                        ? "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        : "border-slate-200 bg-slate-50 opacity-60 hover:opacity-90 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: vColor }}
                    >
                      {globalModeNumber(mode.id) + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-semibold ${isRetained ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                          Mode {globalModeNumber(mode.id) + 1}
                        </span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ backgroundColor: vBgColor, color: vColor }}
                        >
                          {getVraisemblanceLabel(vraisemblance)}
                        </span>
                        {!isRetained && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-slate-200 text-slate-500">
                            {t('workshop4.modesOperatoires.notRetained', 'Non retenu')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {mode.path?.length || 0}{" "}
                        {t(
                          "workshop4.modesOperatoires.elementaryActions",
                          "elementary actions",
                        )}
                      </div>
                    </div>
                    {onToggleRetained && (
                      <span
                        onClick={(e) => { e.stopPropagation(); onToggleRetained(mode.id); }}
                        className={`flex-shrink-0 p-1 rounded cursor-pointer transition-colors ${
                          isRetained
                            ? 'text-emerald-600 hover:bg-emerald-100'
                            : 'text-slate-400 hover:bg-slate-200 hover:text-emerald-600'
                        }`}
                        title={isRetained
                          ? t('workshop4.modesOperatoires.retained', 'Retenu')
                          : t('workshop4.modesOperatoires.notRetained', 'Non retenu')}
                      >
                        {isRetained ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected mode detail panel */}
          <div className="flex-1 min-w-0">
            {(() => {
              const activeModeId =
                Array.from(expandedModes)[0] ||
                selectedModeId ||
                modesOperatoires[0]?.id;
              const activeMode = modesOperatoires.find(
                (m) => m.id === activeModeId,
              );
              if (!activeMode) return null;

              const vraisemblance = activeMode.vraisemblance || 2;
              const vColor = getVraisemblanceColor(vraisemblance);
              const vBgColor = getVraisemblanceBgColor(vraisemblance);
              const riskLevel = getRiskLevel(vraisemblance);

              return (
                <motion.div
                  key={activeMode.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg border-2 border-indigo-400 shadow-md overflow-hidden flex flex-col max-h-[600px]"
                >
                  {/* Mode Header */}
                  <div className="p-3 bg-indigo-50 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                        style={{ backgroundColor: vColor }}
                      >
                        {globalModeNumber(activeMode.id) + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">
                            Mode {globalModeNumber(activeMode.id) + 1}
                          </span>
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-bold"
                            style={{ backgroundColor: vBgColor, color: vColor }}
                          >
                            {getVraisemblanceLabel(vraisemblance)}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {activeMode.path?.length || 0}{" "}
                          {t(
                            "workshop4.modesOperatoires.elementaryActions",
                            "elementary actions",
                          )}
                        </div>
                      </div>
                      <div
                        className="flex items-center gap-1 px-2 py-1 rounded"
                        style={{ backgroundColor: vBgColor }}
                      >
                        <span style={{ color: vColor }}>
                          {getRiskIcon(vraisemblance)}
                        </span>
                        <span
                          className="text-xs font-semibold"
                          style={{ color: vColor }}
                        >
                          {riskLevel}
                        </span>
                      </div>
                      {onToggleRetained && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onToggleRetained(activeMode.id); }}
                          className={`p-1.5 rounded transition-colors ${
                            activeMode.retained === false
                              ? 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
                              : 'text-emerald-600 bg-emerald-50 hover:text-emerald-700'
                          }`}
                          title={activeMode.retained === false
                            ? t('workshop4.modesOperatoires.notRetained', 'Non retenu')
                            : t('workshop4.modesOperatoires.retained', 'Retenu')}
                        >
                          {activeMode.retained === false
                            ? <XCircle size={14} />
                            : <CheckCircle size={14} />}
                        </button>
                      )}
                      {onDeleteMode && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteMode(activeMode.id); }}
                          className="p-1.5 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
                          title="Supprimer ce mode"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mode Details */}
                  <div className="p-4 bg-slate-50 overflow-y-auto">
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-slate-700 mb-2">
                        {t(
                          "workshop4.modesOperatoires.attackPath",
                          "Attack path:",
                        )}
                      </div>
                      <div className="space-y-2">
                        {activeMode.path?.map((actionId, idx) => {
                          const action = getActionDetails(actionId);
                          if (!action) {
                            return (
                              <div key={actionId} className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-white">
                                  {idx + 1}
                                </div>
                                <div className="ml-3 flex-1">
                                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                    {actionId}
                                  </span>
                                </div>
                                {idx < activeMode.path.length - 1 && (
                                  <div className="ml-3 text-slate-400">↓</div>
                                )}
                              </div>
                            );
                          }

                          const actionPS = action.ps || action.successProbability || 2;
                          const actionDT = action.dt || action.technicalDifficulty || 2;

                          const getPhaseColor = (phase) => {
                            switch (phase) {
                              case "CONNAITRE":
                                return "bg-blue-100 text-blue-700 border-blue-300";
                              case "RENTRER":
                                return "bg-orange-100 text-orange-700 border-orange-300";
                              case "TROUVER":
                                return "bg-purple-100 text-purple-700 border-purple-300";
                              case "EXPLOITER":
                                return "bg-red-100 text-red-700 border-red-300";
                              default:
                                return "bg-slate-100 text-slate-700 border-slate-300";
                            }
                          };

                          return (
                            <div
                              key={actionId}
                              className="flex items-start group"
                            >
                              <div
                                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white bg-slate-400"
                              >
                                {idx + 1}
                              </div>
                              <div className="ml-3 flex-1">
                                <div className="bg-white rounded-lg border-2 border-slate-200 p-2 group-hover:border-slate-300 group-hover:shadow-sm transition-all">
                                  <div className="flex items-center justify-between mb-1">
                                    <span
                                      className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${getPhaseColor(action.phase)}`}
                                    >
                                      {action.phase}
                                    </span>
                                    <div className="flex gap-1">
                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-100 text-blue-700">
                                        PS:{actionPS}/4
                                      </span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-orange-100 text-orange-700">
                                        DT:{actionDT}/4
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-xs font-semibold text-slate-800 mb-1">
                                    {action.technique ||
                                      action.name ||
                                      t(
                                        "workshop4.modesOperatoires.unnamedAction",
                                        "Unnamed action",
                                      )}
                                  </div>
                                  <div className="text-[11px] text-slate-600 line-clamp-2">
                                    {action.description}
                                  </div>
                                  {(action.tools?.length > 0 ||
                                    action.duration) && (
                                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                                      {action.tools?.length > 0 && (
                                        <span className="flex items-center">
                                          🛠️{" "}
                                          {action.tools.slice(0, 2).join(", ")}
                                          {action.tools.length > 2 &&
                                            ` +${action.tools.length - 2}`}
                                        </span>
                                      )}
                                      {action.duration && (
                                        <span className="flex items-center">
                                          ⏱️ {action.duration}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {selectedAttackPath && (
                                    <CTIContext
                                      techniqueName={action.technique}
                                      ctiData={selectedAttackPath}
                                    />
                                  )}
                                  <div className="mt-1 text-right">
                                    <span className="text-[10px] text-slate-400 font-mono">
                                      {action.elementaryActionId || actionId}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {idx < activeMode.path.length - 1 && (
                                <div className="ml-3 mt-2 text-slate-400 font-bold text-lg">
                                  ↓
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white rounded-lg p-2 border border-slate-200">
                        <div className="text-xs text-slate-500">Actions</div>
                        <div className="text-lg font-bold text-slate-800">
                          {activeMode.path?.length || 0}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 border border-slate-200">
                        <div className="text-xs text-slate-500">
                          Vraisemblance
                        </div>
                        <div
                          className="text-lg font-bold"
                          style={{ color: vColor }}
                        >
                          {vraisemblance}/4
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 border border-slate-200">
                        <div className="text-xs text-slate-500">
                          {t("workshop4.modesOperatoires.level", "Level")}
                        </div>
                        <div
                          className="text-sm font-bold"
                          style={{ color: vColor }}
                        >
                          {riskLevel}
                        </div>
                      </div>
                    </div>

                    {/* Phases Breakdown */}
                    {activeMode.path && (
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-slate-700 mb-2">
                          {t(
                            "workshop4.modesOperatoires.distributionByPhase",
                            "Distribution by phase:",
                          )}
                        </div>
                        <div className="flex space-x-1">
                          {["CONNAITRE", "RENTRER", "TROUVER", "EXPLOITER"].map(
                            (phase) => {
                              const phaseActions = activeMode.path.filter(
                                (actionId) => {
                                  const action = getActionDetails(actionId);
                                  return action?.phase === phase;
                                },
                              );
                              const count = phaseActions.length;
                              return (
                                <div
                                  key={phase}
                                  className="flex-1 bg-white rounded p-1.5 border border-slate-200 text-center"
                                >
                                  <div className="text-[10px] text-slate-600 font-medium">
                                    {phase.substring(0, 4)}
                                  </div>
                                  <div className="text-sm font-bold text-slate-800">
                                    {count}
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {modesOperatoires.map((mode, index) => {
            const isExpanded = expandedModes.has(mode.id);
            const isSelected = selectedModeId === mode.id;
            const isRetained = mode.retained !== false;
            const vraisemblance = mode.vraisemblance || 2;
            const vColor = getVraisemblanceColor(vraisemblance);
            const vBgColor = getVraisemblanceBgColor(vraisemblance);
            const riskLevel = getRiskLevel(vraisemblance);

            return (
              <motion.div
                key={mode.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-lg border-2 overflow-hidden transition-all ${
                  isSelected
                    ? "border-indigo-400 shadow-md"
                    : isRetained
                      ? "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                      : "border-slate-200 opacity-60 hover:opacity-90"
                }`}
              >
                {/* Mode Header */}
                <div
                  className={`p-3 cursor-pointer ${
                    isSelected ? "bg-indigo-50" : "bg-white hover:bg-slate-50"
                  }`}
                  onClick={() => {
                    if (onSelectMode) onSelectMode(mode.id);
                    toggleExpand(mode.id);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                        style={{ backgroundColor: vColor }}
                      >
                        {globalModeNumber(mode.id) + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${isRetained ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                            Mode {globalModeNumber(mode.id) + 1}
                          </span>
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-bold"
                            style={{ backgroundColor: vBgColor, color: vColor }}
                          >
                            {getVraisemblanceLabel(vraisemblance)}
                          </span>
                          {!isRetained && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-slate-200 text-slate-500 uppercase tracking-wider">
                              {t('workshop4.modesOperatoires.notRetained', 'Non retenu')}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {mode.path?.length || 0}{" "}
                          {t(
                            "workshop4.modesOperatoires.elementaryActions",
                            "elementary actions",
                          )}
                        </div>
                      </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center gap-1 px-2 py-1 rounded"
                            style={{ backgroundColor: vBgColor }}
                          >
                            <span style={{ color: vColor }}>
                              {getRiskIcon(vraisemblance)}
                            </span>
                            <span
                              className="text-xs font-semibold"
                              style={{ color: vColor }}
                            >
                              {riskLevel}
                            </span>
                          </div>
                          {onToggleRetained && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onToggleRetained(mode.id); }}
                              className={`p-1.5 rounded transition-colors ${
                                mode.retained === false
                                  ? 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
                                  : 'text-emerald-600 bg-emerald-50 hover:text-emerald-700'
                              }`}
                              title={mode.retained === false
                                ? t('workshop4.modesOperatoires.notRetained', 'Non retenu')
                                : t('workshop4.modesOperatoires.retained', 'Retenu')}
                            >
                              {mode.retained === false
                                ? <XCircle size={14} />
                                : <CheckCircle size={14} />}
                            </button>
                          )}
                          {onDeleteMode && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onDeleteMode(mode.id); }}
                              className="p-1.5 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
                              title="Supprimer ce mode"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      <button
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(mode.id);
                        }}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mode Details (Expanded) */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-slate-200"
                    >
                      <div className="p-4 bg-slate-50 max-h-[600px] overflow-y-auto">
                        {/* Attack Path */}
                        <div className="mb-3">
                          <div className="text-xs font-semibold text-slate-700 mb-2">
                            {t(
                              "workshop4.modesOperatoires.attackPath",
                              "Attack path:",
                            )}
                          </div>
                          <div className="space-y-2">
                            {mode.path?.map((actionId, idx) => {
                              const action = getActionDetails(actionId);
                              if (!action) {
                                return (
                                  <div
                                    key={actionId}
                                    className="flex items-start"
                                  >
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-white">
                                      {idx + 1}
                                    </div>
                                    <div className="ml-3 flex-1">
                                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                                        {actionId}
                                      </span>
                                    </div>
                                    {idx < mode.path.length - 1 && (
                                      <div className="ml-3 text-slate-400">
                                        ↓
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              const actionPS = action.ps || action.successProbability || 2;
                              const actionDT = action.dt || action.technicalDifficulty || 2;

                              const getPhaseColor = (phase) => {
                                switch (phase) {
                                  case "CONNAITRE":
                                    return "bg-blue-100 text-blue-700 border-blue-300";
                                  case "RENTRER":
                                    return "bg-orange-100 text-orange-700 border-orange-300";
                                  case "TROUVER":
                                    return "bg-purple-100 text-purple-700 border-purple-300";
                                  case "EXPLOITER":
                                    return "bg-red-100 text-red-700 border-red-300";
                                  default:
                                    return "bg-slate-100 text-slate-700 border-slate-300";
                                }
                              };

                              return (
                                <div
                                  key={actionId}
                                  className="flex items-start group"
                                >
                                  <div
                                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white bg-slate-400"
                                  >
                                    {idx + 1}
                                  </div>
                                  <div className="ml-3 flex-1">
                                    <div className="bg-white rounded-lg border-2 border-slate-200 p-2 group-hover:border-slate-300 group-hover:shadow-sm transition-all">
                                      <div className="flex items-center justify-between mb-1">
                                        <span
                                          className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${getPhaseColor(action.phase)}`}
                                        >
                                          {action.phase}
                                        </span>
                                        <div className="flex gap-1">
                                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-100 text-blue-700">
                                            PS:{actionPS}/4
                                          </span>
                                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-orange-100 text-orange-700">
                                            DT:{actionDT}/4
                                          </span>
                                        </div>
                                      </div>
                                      <div className="text-xs font-semibold text-slate-800 mb-1">
                                        {action.technique ||
                                          action.name ||
                                          t(
                                            "workshop4.modesOperatoires.unnamedAction",
                                            "Unnamed action",
                                          )}
                                      </div>
                                      <div className="text-[11px] text-slate-600 line-clamp-2">
                                        {action.description}
                                      </div>
                                      {(action.tools?.length > 0 ||
                                        action.duration) && (
                                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                                          {action.tools?.length > 0 && (
                                            <span className="flex items-center">
                                              🛠️{" "}
                                              {action.tools
                                                .slice(0, 2)
                                                .join(", ")}
                                              {action.tools.length > 2 &&
                                                ` +${action.tools.length - 2}`}
                                            </span>
                                          )}
                                          {action.duration && (
                                            <span className="flex items-center">
                                              ⏱️ {action.duration}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      {selectedAttackPath && (
                                        <CTIContext
                                          techniqueName={action.technique}
                                          ctiData={selectedAttackPath}
                                        />
                                      )}
                                      <div className="mt-1 text-right">
                                        <span className="text-[10px] text-slate-400 font-mono">
                                          {action.elementaryActionId || actionId}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  {idx < mode.path.length - 1 && (
                                    <div className="ml-3 mt-2 text-slate-400 font-bold text-lg">
                                      ↓
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Statistics */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white rounded-lg p-2 border border-slate-200">
                            <div className="text-xs text-slate-500">
                              Actions
                            </div>
                            <div className="text-lg font-bold text-slate-800">
                              {mode.path?.length || 0}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-2 border border-slate-200">
                            <div className="text-xs text-slate-500">
                              Vraisemblance
                            </div>
                            <div
                              className="text-lg font-bold"
                              style={{ color: vColor }}
                            >
                              {vraisemblance}/4
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-2 border border-slate-200">
                            <div className="text-xs text-slate-500">
                              {t("workshop4.modesOperatoires.level", "Level")}
                            </div>
                            <div
                              className="text-sm font-bold"
                              style={{ color: vColor }}
                            >
                              {riskLevel}
                            </div>
                          </div>
                        </div>

                        {/* Phases Breakdown */}
                        {mode.path && (
                          <div className="mt-3">
                            <div className="text-xs font-semibold text-slate-700 mb-2">
                              {t(
                                "workshop4.modesOperatoires.distributionByPhase",
                                "Distribution by phase:",
                              )}
                            </div>
                            <div className="flex space-x-1">
                              {[
                                "CONNAITRE",
                                "RENTRER",
                                "TROUVER",
                                "EXPLOITER",
                              ].map((phase) => {
                                const phaseActions = mode.path.filter(
                                  (actionId) => {
                                    const action = getActionDetails(actionId);
                                    return action?.phase === phase;
                                  },
                                );
                                const count = phaseActions.length;
                                return (
                                  <div
                                    key={phase}
                                    className="flex-1 bg-white rounded p-1.5 border border-slate-200 text-center"
                                  >
                                    <div className="text-[10px] text-slate-600 font-medium">
                                      {phase.substring(0, 4)}
                                    </div>
                                    <div className="text-sm font-bold text-slate-800">
                                      {count}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Summary removed as per user request */}

      {/* Inline Flow Visualization */}
      <div className="mt-6">
        <ModesOperatoiresFlowVisualization
          scenario={scenario}
          modesOperatoires={vizModes || modesOperatoires}
          operationalSteps={visualizationSteps}
          isInline={true}
          onCaptureReady={onCaptureReady}
        />
      </div>
    </div>
  );
};

export default ModesOperatoiresDisplay;
